import { JSX } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { Label } from 'components/Label';
import { Select, SelectValue } from 'components/Select';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { Input } from 'components/input/Input';
import { Divider } from 'components/layout/Divider';
import { Typography } from 'components/typography';
import { CostLineItem, NormalizedLoad } from 'types/Load';
import { MaybeUndef, Undef } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from './UnifiedAljexLoadForm';
import {
  ALJEX_CARRIER_RATE_TYPE_LABELS,
  ALJEX_CUSTOMER_RATE_TYPE_LABELS,
} from './constants';
import {
  getAljexRateTypeSelectOptions,
  normalizeAljexRateType,
  normalizeAljexRateTypeFromSelect,
} from './utils';

export function isFuelFlatLineItem(item: MaybeUndef<CostLineItem>): boolean {
  return (
    !!item?.label?.toLowerCase().includes('fuel') &&
    item?.unitBasis?.toLowerCase() === 'flat'
  );
}

export function getFuelFlatDisplayValue(
  items: Undef<CostLineItem[]>
): number | '' {
  const item = (items || []).find(isFuelFlatLineItem);
  if (!item) return '';
  const total =
    typeof item.totalChargeUSD === 'number' &&
    Number.isFinite(item.totalChargeUSD)
      ? item.totalChargeUSD
      : null;
  const rate =
    typeof item.ratePerUnitUSD === 'number' &&
    Number.isFinite(item.ratePerUnitUSD)
      ? item.ratePerUnitUSD
      : null;
  const val = total ?? rate;
  return val === null ? '' : val;
}

function rateTypeFromSelectChange(value: SelectValue): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return normalizeAljexRateTypeFromSelect(value);
  }
  return '';
}

export function withUpdatedFuelItem(
  items: CostLineItem[],
  quantity: number | ''
): CostLineItem[] {
  const withoutFuel = (items || []).filter((i) => !isFuelFlatLineItem(i));
  const num =
    typeof quantity === 'number' && !Number.isNaN(quantity) ? quantity : null;
  if (num === null) return withoutFuel;
  return [
    ...withoutFuel,
    {
      label: 'FUEL',
      unitBasis: 'flat',
      quantity: 1,
      ratePerUnitUSD: num,
      totalChargeUSD: num,
      note: '',
    },
  ];
}

export default function RatesForm({
  formMethods,
  isCreateMode = true,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  isCreateMode: boolean;
}): JSX.Element {
  const { control, watch } = formMethods;

  // const watchedCustomerTotalCharge = watch('rateData.customerTotalCharge.val');
  const watchedCustomerLineHaulRate = watch('rateData.customerLineHaulRate');
  const watchedCarrierLineHaulRate = watch('rateData.carrierLineHaulRate');
  // const watchedFscPercent = watch('rateData.fscPercent');
  // const watchedFscPerMile = watch('rateData.fscPerMile');

  const watchedCustomerRateType = watch('rateData.customerRateType');
  const watchedCarrierRateType = watch('rateData.carrierRateType');

  const hasCustomerRates = (watchedCustomerLineHaulRate ?? 0) > 0;
  const hasCarrierRates = (watchedCarrierLineHaulRate ?? 0) > 0;

  const customerRateTypeOptions = getAljexRateTypeSelectOptions(
    watchedCustomerRateType,
    ALJEX_CUSTOMER_RATE_TYPE_LABELS
  );
  const carrierRateTypeOptions = getAljexRateTypeSelectOptions(
    watchedCarrierRateType,
    ALJEX_CARRIER_RATE_TYPE_LABELS
  );

  return (
    <div className='grid grid-cols-2 gap-4 mx-0 w-full'>
      {/* Customer Section */}
      <>
        <div className='col-span-2 text-md text-neutral-700 font-semibold mb-2'>
          Customer Rates
        </div>

        <div className='col-span-1'>
          <Label name={'rateData.customerRateType'} required={hasCustomerRates}>
            Rate Type
          </Label>
          <Controller
            name='rateData.customerRateType'
            control={control}
            rules={{
              required: hasCustomerRates
                ? 'Required when customer rates are entered'
                : false,
            }}
            render={({ field }) => (
              <Select
                showSearch
                className='text-neutral-600'
                placeholder='Choose rate type'
                optionFilterProp='children'
                onChange={(value) =>
                  field.onChange(rateTypeFromSelectChange(value))
                }
                value={normalizeAljexRateType(field.value) || field.value}
                options={customerRateTypeOptions}
              />
            )}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='rateData.customerRateNumUnits'
            label='# of Hours'
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
            placeholder='e.g., hours for hourly rate'
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='rateData.customerLineHaulRate'
            label='Line Haul Rate'
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Controller
            name='rateData.customerLineItems'
            control={control}
            render={({ field }) => {
              const fuelQty = getFuelFlatDisplayValue(field.value);
              return (
                <FormInputWrapper
                  name='rateData.customerLineItems'
                  label='Fuel Flat Rate'
                  required={false}
                >
                  <div className='relative'>
                    <Input
                      name={field.name}
                      type='number'
                      step={0.01}
                      min={0}
                      value={fuelQty === '' ? '' : fuelQty}
                      onChange={(e) => {
                        const val = e.target.valueAsNumber;
                        field.onChange(
                          withUpdatedFuelItem(field.value || [], val)
                        );
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                  </div>
                </FormInputWrapper>
              );
            }}
          />
        </div>

        {!isCreateMode && (
          <>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.customerLineHaulCharge.val'
                label='Total LH'
                inputType='number'
                readOnly={true}
                required={false}
              />
            </div>

            <div className='col-span-1 col-start-2'>
              <LoadBuildingTextInput
                name='rateData.customerTotalCharge.val'
                label='Total Cost'
                inputType='number'
                readOnly={true}
                required={false}
              />
            </div>
          </>
        )}
      </>

      <>
        {/* Carrier Section */}
        <div className='col-span-2 text-md text-neutral-700 font-semibold mb-2 mt-4'>
          Carrier Rates
        </div>

        <div className='col-span-1'>
          <Label name={'rateData.carrierRateType'} required={hasCarrierRates}>
            Rate Type
          </Label>
          <Controller
            name='rateData.carrierRateType'
            control={control}
            rules={{
              required: hasCarrierRates
                ? 'Required when carrier rates are entered'
                : false,
            }}
            render={({ field }) => (
              <Select
                showSearch
                className='text-neutral-600'
                placeholder='Choose rate type'
                optionFilterProp='children'
                onChange={(value) =>
                  field.onChange(rateTypeFromSelectChange(value))
                }
                value={normalizeAljexRateType(field.value) || field.value}
                options={carrierRateTypeOptions}
              />
            )}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='rateData.carrierRateNumUnits'
            label='# of Hours'
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
            placeholder='e.g., hours for hourly rate'
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='rateData.carrierLineHaulRate'
            label='Line Haul Rate'
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Controller
            name='rateData.carrierLineItems'
            control={control}
            render={({ field }) => {
              const fuelQty = getFuelFlatDisplayValue(field.value);
              return (
                <FormInputWrapper
                  name='rateData.carrierLineItems'
                  label='Fuel Flat Rate'
                  required={false}
                >
                  <div className='relative'>
                    <Input
                      name={field.name}
                      type='number'
                      step={0.01}
                      min={0}
                      value={fuelQty === '' ? '' : fuelQty}
                      onChange={(e) => {
                        const val = e.target.valueAsNumber;
                        field.onChange(
                          withUpdatedFuelItem(field.value || [], val)
                        );
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                  </div>
                </FormInputWrapper>
              );
            }}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='rateData.carrierMaxRate'
            label='Max Rate'
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
          />
        </div>

        {!isCreateMode && (
          <>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.carrierLineHaulCharge.val'
                label='Total LH'
                inputType='number'
                readOnly={true}
                required={false}
              />
            </div>

            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.carrierTotalCost.val'
                label='Total Cost'
                inputType='number'
                readOnly={true}
                required={false}
              />
            </div>
          </>
        )}
      </>

      {/* FSC Section */}
      <Typography variant='h6' weight='semibold' className='col-span-2'>
        Fuel Surcharge
      </Typography>

      <div className='col-span-1'>
        <LoadBuildingTextInput
          name='rateData.fscPercent'
          label='FSC Percentage'
          inputType='number'
          options={{ valueAsNumber: true }}
          required={false}
          placeholder='0-100'
        />
      </div>

      <div className='col-span-1'>
        <LoadBuildingTextInput
          name='rateData.fscPerMile'
          label='FSC Per Mile'
          inputType='number'
          options={{ valueAsNumber: true }}
          required={false}
        />
      </div>

      <Divider size='md' className='col-span-2 m-0' />

      {/* Profit Section */}
      <Typography variant='h6' weight='semibold' className='col-span-2'>
        Profit (Aljex-calculated)
      </Typography>
      <div className='col-span-1'>
        <LoadBuildingTextInput
          name='rateData.netProfitUSD'
          label='Net Profit'
          inputType='number'
          options={{ valueAsNumber: true }}
          required={false}
          readOnly={true}
        />
      </div>
      <div className='col-span-1'>
        <LoadBuildingTextInput
          name='rateData.profitPercent'
          label='Profit Percent'
          inputType='number'
          options={{ valueAsNumber: true }}
          required={false}
          readOnly={true}
        />
      </div>
    </div>
  );
}
